/**
 * ChatError — the ONE shape every failed turn is normalised to.
 *
 * A turn can fail in four different places: the socket's `error` frame
 * (`{ code, description }`), a POST that
 * returned 200 with `has_error` + `status_code`, a POST that rejected outright,
 * and a persisted row read back on reload. All four carry the SAME information —
 * a BE status code plus optional detail — so all four normalise here, at the
 * boundary they enter through (see [parseChatError](../lib/errors/parseChatError.ts)).
 */

/**
 * The app-level statuses that mean "the session is not valid" — the ONLY failure safe
 * to retry automatically, because the request was refused before it ran (see
 * [tokenRefreshMiddleware](../commands/middleware/tokenRefreshMiddleware.ts)).
 */
export const AUTH_FAILURE_STATUS = {
  INVALID_USER: 'INVALID_USER',
  INVALID_TOKEN: 'INVALID_TOKEN',
  SESSION_EXPIRED: 'SESSION_EXPIRED'
} as const;

export const STOPPED_STATUS_CODE = 'CANCELLED';

/** Where the failure was observed. Diagnostics + analytics only — the RENDERED
 * card is identical for every origin (the user does not care which layer noticed
 * that their turn died). */
export const CHAT_ERROR_ORIGIN = {
  RUN: 'run',
  HTTP: 'http',
  TRANSPORT: 'transport',
  PERSISTED: 'persisted'
} as const;

export type ChatErrorOrigin =
  (typeof CHAT_ERROR_ORIGIN)[keyof typeof CHAT_ERROR_ORIGIN];

export interface ChatError {
  code: string;
  origin: ChatErrorOrigin;
  details?: string;
}
